import React from 'react';
import { Student } from './hooks';
import TrashIcon from '../../../../components/icons/TrashIcon';

// 커스텀 스크롤바 스타일
const scrollbarStyles = `
  .approval-table-scrollbar::-webkit-scrollbar {
    width: 8px;
  }
  
  .approval-table-scrollbar::-webkit-scrollbar-track {
    background: transparent;
  }
  
  .approval-table-scrollbar::-webkit-scrollbar-thumb {
    background: #D9D9D9;
    border-radius: 4px;
  }
  
  .approval-table-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #C0C0C0;
  }
`;

export interface PointApproval {
  id: string;
  uid: string;
  roleId: string;
  salaryType: 'point' | 'stamp';
  salaryPeriod: 'daily' | 'weekly' | 'monthly';
  targetDate: Date;
  isApproved: boolean;
  pointAutoPay: boolean;
  pointManagerApproved: boolean | null;
  teacherApproved: boolean | null;
  pointManagerApprovedAt: Date | null;
  teacherApprovedAt: Date | null;
  createdAt: Date;
  updatedAt: Date;
}

export interface ApprovalTableProps {
  approvals: PointApproval[];
  students: Student[];
  getStudentName: (uid: string) => string;
  getRoleName: (roleId: string) => string;
  getPointAmount: (roleId: string) => number | undefined;
  getPointAutoPay: (roleId: string) => boolean | undefined;
  getPointStatus: (approval: PointApproval) => { label: string; color: 'success' | 'error' | 'warning' } | null;
  handleOpenInfoDialog: (approval: PointApproval) => void;
  handleApprove: (approvalId: string) => void;
  handleReject: (approvalId: string) => void;
  handleDelete: (approvalId: string) => void;
  selectedIds: string[];
  onSelect: (id: string, checked: boolean) => void;
  onSelectAll: (checked: boolean) => void;
  /** 세금 설정 반영 후 예상 실지급 포인트(역할 급여 총액과 다를 때 표시) */
  getEstimatedNet?: (roleId: string) => number;
}

const ApprovalTable: React.FC<ApprovalTableProps> = ({
  approvals,
  students,
  getStudentName,
  getRoleName,
  getPointAmount,
  getPointAutoPay,
  getPointStatus,
  handleOpenInfoDialog,
  handleApprove,
  handleReject,
  handleDelete,
  selectedIds,
  onSelect,
  onSelectAll,
  getEstimatedNet
}) => {
  const pxToRem = (value: number) => `${value / 16}rem`;

  const allSelected = approvals.length > 0 && selectedIds.length === approvals.length;
  const someSelected = selectedIds.length > 0 && selectedIds.length < approvals.length;

  // 체크박스 렌더링 함수
  const renderCheckbox = (checked: boolean, onChange: (checked: boolean) => void, indeterminate?: boolean) => {
    return (
      <div
        onClick={() => onChange(!checked)}
        style={{
          justifyContent: 'flex-start',
          alignItems: 'flex-start',
          display: 'flex',
          cursor: 'pointer'
        }}
      >
        <div
          style={{
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: pxToRem(4),
            display: 'flex'
          }}
        >
          <div
            style={{
              justifyContent: 'flex-start',
              alignItems: 'flex-start',
              display: 'flex'
            }}
          >
            <div
              style={{
                justifyContent: 'center',
                alignItems: 'center',
                display: 'flex'
              }}
            >
              {checked || indeterminate ? (
                // 체크된 상태 (오렌지색 원)
                <div
                  style={{
                    width: pxToRem(16),
                    height: pxToRem(16),
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    gap: pxToRem(10),
                    display: 'flex'
                  }}
                >
                  <div
                    style={{
                      flex: '1 1 0',
                      alignSelf: 'stretch',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                  >
                    <div
                      style={{
                        width: pxToRem(16),
                        height: pxToRem(16),
                        left: 0,
                        top: 0,
                        position: 'absolute',
                        background: 'var(--Color-text-brand-color, #FF6F0F)',
                        borderRadius: '50%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}
                    >
                      {/* 체크 마크 */}
                      <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8 2.5L4 6.5L2 4.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  </div>
                </div>
              ) : (
                // 체크 안된 상태 (흰색 배경, 회색 테두리)
                <div
                  style={{
                    width: pxToRem(16),
                    height: pxToRem(16),
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: pxToRem(10),
                    display: 'flex'
                  }}
                >
                  <div
                    style={{
                      flex: '1 1 0',
                      alignSelf: 'stretch',
                      position: 'relative'
                    }}
                  >
                    <div
                      style={{
                        width: pxToRem(16),
                        height: pxToRem(16),
                        left: 0,
                        top: 0,
                        position: 'absolute',
                        background: 'var(--Color-fill-white, white)',
                        border: '1px solid var(--Color-fill-inactive, #F5F5F5)',
                        borderRadius: pxToRem(2)
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // 상태 아이콘 렌더링 함수
  const renderStatusIcon = (status: 'pending' | 'approved' | 'rejected' | 'waiting') => {
    switch (status) {
      case 'approved':
        return (
          <div style={{
            width: pxToRem(16),
            height: pxToRem(16),
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="8" cy="8" r="7" fill="var(--Color-status-positive-2, #00B66A)"/>
              <path d="M5 8L7 10L11 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        );
      case 'rejected':
      case 'waiting':
        return (
          <div style={{
            width: pxToRem(16),
            height: pxToRem(16),
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="8" cy="8" r="7" fill="var(--Color-status-negative, #FF3838)"/>
              <path d="M6 6L10 10M10 6L6 10" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <style>{scrollbarStyles}</style>
      <div style={{
        alignSelf: 'stretch',
        height: pxToRem(514),
        paddingLeft: pxToRem(20),
        paddingRight: pxToRem(20),
        paddingTop: pxToRem(16),
        paddingBottom: pxToRem(16),
        background: 'var(--Color-fill-white, white)',
        borderRadius: pxToRem(16),
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        gap: pxToRem(10),
        display: 'flex',
        overflow: 'hidden'
      }}>
      {/* 테이블 헤더 */}
      <div style={{
        alignSelf: 'stretch',
        borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
        justifyContent: 'flex-start',
        alignItems: 'center',
        display: 'inline-flex',
        flexShrink: 0
      }}>
        {/* 체크박스 + No */}
        <div style={{
          width: pxToRem(103),
          paddingBottom: pxToRem(12),
          paddingLeft: pxToRem(16),
          paddingRight: pxToRem(16),
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: pxToRem(12),
          display: 'flex'
        }}>
          {renderCheckbox(allSelected, onSelectAll, someSelected)}
          <div style={{
            color: 'var(--Color-text-assistive, #989898)',
            fontSize: pxToRem(13),
            fontFamily: 'Pretendard, sans-serif',
            fontWeight: '500',
            lineHeight: pxToRem(19.5),
            wordWrap: 'break-word'
          }}>
            No
          </div>
        </div>
        {/* 이름 */}
        <div style={{
          width: pxToRem(103),
          paddingBottom: pxToRem(12),
          paddingLeft: pxToRem(8),
          paddingRight: pxToRem(8),
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: pxToRem(2),
          display: 'flex'
        }}>
          <div style={{
            color: 'var(--Color-text-assistive, #989898)',
            fontSize: pxToRem(13),
            fontFamily: 'Pretendard, sans-serif',
            fontWeight: '500',
            lineHeight: pxToRem(19.5),
            wordWrap: 'break-word'
          }}>
            이름
          </div>
        </div>
        {/* 역할 */}
        <div style={{
          width: pxToRem(150),
          paddingBottom: pxToRem(12),
          paddingLeft: pxToRem(8),
          paddingRight: pxToRem(8),
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: pxToRem(2),
          display: 'flex'
        }}>
          <div style={{
            color: 'var(--Color-text-assistive, #989898)',
            fontSize: pxToRem(13),
            fontFamily: 'Pretendard, sans-serif',
            fontWeight: '500',
            lineHeight: pxToRem(19.5),
            wordWrap: 'break-word'
          }}>
            역할
          </div>
        </div>
        {/* 포인트 */}
        <div style={{
          width: pxToRem(139),
          paddingBottom: pxToRem(12),
          paddingLeft: pxToRem(8),
          paddingRight: pxToRem(8),
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: pxToRem(2),
          display: 'flex'
        }}>
          <div style={{
            color: 'var(--Color-text-assistive, #989898)',
            fontSize: pxToRem(13),
            fontFamily: 'Pretendard, sans-serif',
            fontWeight: '500',
            lineHeight: pxToRem(19.5),
            wordWrap: 'break-word'
          }}>
            포인트
          </div>
        </div>
        {/* 포인트 관리자 승인 */}
        <div style={{
          width: pxToRem(290),
          paddingBottom: pxToRem(12),
          paddingLeft: pxToRem(8),
          paddingRight: pxToRem(8),
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: pxToRem(2),
          display: 'flex'
        }}>
          <div style={{
            color: 'var(--Color-text-assistive, #989898)',
            fontSize: pxToRem(13),
            fontFamily: 'Pretendard, sans-serif',
            fontWeight: '500',
            lineHeight: pxToRem(19.5),
            wordWrap: 'break-word'
          }}>
            포인트 관리자 승인
          </div>
        </div>
        {/* 교사 최종 승인 */}
        <div style={{
          width: pxToRem(290),
          paddingBottom: pxToRem(12),
          paddingLeft: pxToRem(8),
          paddingRight: pxToRem(8),
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: pxToRem(8),
          display: 'flex'
        }}>
          <div style={{
            color: 'var(--Color-text-assistive, #989898)',
            fontSize: pxToRem(13),
            fontFamily: 'Pretendard, sans-serif',
            fontWeight: '500',
            lineHeight: pxToRem(19.5),
            wordWrap: 'break-word'
          }}>
            교사 최종 승인
          </div>
        </div>
        {/* 승인 */}
        <div style={{
          width: pxToRem(110),
          paddingBottom: pxToRem(12),
          paddingLeft: pxToRem(8),
          paddingRight: pxToRem(8),
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: pxToRem(8),
          display: 'flex'
        }}>
          <div style={{
            color: 'var(--Color-text-assistive, #989898)',
            fontSize: pxToRem(13),
            fontFamily: 'Pretendard, sans-serif',
            fontWeight: '500',
            lineHeight: pxToRem(19.5),
            wordWrap: 'break-word'
          }}>
            승인
          </div>
        </div>
        {/* 관리 */}
        <div style={{
          width: pxToRem(55),
          paddingBottom: pxToRem(12),
          paddingLeft: pxToRem(8),
          paddingRight: pxToRem(8),
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: pxToRem(8),
          display: 'flex'
        }}>
          <div style={{
            color: 'var(--Color-text-assistive, #989898)',
            fontSize: pxToRem(13),
            fontFamily: 'Pretendard, sans-serif',
            fontWeight: '500',
            lineHeight: pxToRem(19.5),
            wordWrap: 'break-word'
          }}>
            관리
          </div>
        </div>
      </div>

      {/* 테이블 본문 */}
      <div 
        className="approval-table-scrollbar"
        style={{
          alignSelf: 'stretch',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'flex-start',
          display: 'flex',
          overflowY: 'auto',
          overflowX: 'hidden',
          flex: '1 1 0',
          minHeight: 0
        }}
      >
        {approvals.length === 0 ? (
          <div style={{
            alignSelf: 'stretch',
            padding: pxToRem(24),
            textAlign: 'center',
            color: 'var(--Color-text-normal, #161616)',
            fontSize: pxToRem(14),
            fontFamily: 'Pretendard, sans-serif',
            fontWeight: '400',
            lineHeight: pxToRem(21)
          }}>
            승인 요청이 없습니다.
          </div>
        ) : (
          approvals.map((approval, index) => {
            const pointAutoPay = getPointAutoPay(approval.roleId);
            const pointStatus = getPointStatus(approval);
            const isSelected = selectedIds.includes(approval.id);
            const student = students.find(s => s.uid === approval.uid);
            
            // 포인트 관리자 승인 상태
            const pointManagerStatus = pointAutoPay 
              ? 'approved' 
              : approval.pointManagerApproved === true 
                ? 'approved' 
                : approval.pointManagerApproved === false 
                  ? 'rejected' 
                  : 'pending';
            
            // 교사 최종 승인 상태
            const teacherStatus = pointAutoPay
              ? 'approved'
              : approval.teacherApproved === true
                ? 'approved'
                : approval.teacherApproved === false
                  ? 'rejected'
                  : 'waiting';

            return (
              <div
                key={approval.id}
                style={{
                  alignSelf: 'stretch',
                  borderBottom: index < approvals.length - 1 ? '1px solid var(--Color-line-normal, #F0F0F0)' : 'none',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  display: 'inline-flex'
                }}
              >
                {/* 체크박스 + 번호 */}
                <div style={{
                  width: pxToRem(103),
                  paddingLeft: pxToRem(16),
                  paddingRight: pxToRem(16),
                  paddingTop: pxToRem(12),
                  paddingBottom: pxToRem(12),
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  gap: pxToRem(12),
                  display: 'flex'
                }}>
                  {renderCheckbox(isSelected, (checked) => onSelect(approval.id, checked))}
                  <div style={{
                    color: 'var(--Color-text-normal, #161616)',
                    fontSize: pxToRem(14),
                    fontFamily: 'Pretendard, sans-serif',
                    fontWeight: '400',
                    lineHeight: pxToRem(21),
                    wordWrap: 'break-word'
                  }}>
                    {student?.number || index + 1}
                  </div>
                </div>
                {/* 이름 — 클릭 시 급여 명세(지급 예시) */}
                <div style={{
                  width: pxToRem(103),
                  paddingLeft: pxToRem(8),
                  paddingRight: pxToRem(8),
                  paddingTop: pxToRem(12),
                  paddingBottom: pxToRem(12),
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'flex-start',
                  gap: pxToRem(8),
                  display: 'inline-flex'
                }}>
                  <button
                    type="button"
                    onClick={() => handleOpenInfoDialog(approval)}
                    aria-label={`${getStudentName(approval.uid)} 급여 명세 보기`}
                    style={{
                      alignSelf: 'stretch',
                      color: 'var(--Color-text-normal, #161616)',
                      fontSize: pxToRem(14),
                      fontFamily: 'Pretendard, sans-serif',
                      fontWeight: '400',
                      lineHeight: pxToRem(21),
                      wordWrap: 'break-word',
                      cursor: 'pointer',
                      border: 'none',
                      background: 'none',
                      padding: 0,
                      textAlign: 'left',
                      textDecoration: 'underline',
                      textDecorationColor: 'var(--Color-line-normal, #F0F0F0)',
                      textUnderlineOffset: pxToRem(2),
                    }}
                  >
                    {getStudentName(approval.uid)}
                  </button>
                </div>
                {/* 역할 */}
                <div style={{
                  width: pxToRem(150),
                  paddingLeft: pxToRem(8),
                  paddingRight: pxToRem(8),
                  paddingTop: pxToRem(12),
                  paddingBottom: pxToRem(12),
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'flex-start',
                  gap: pxToRem(8),
                  display: 'inline-flex'
                }}>
                  <div style={{
                    alignSelf: 'stretch',
                    color: 'var(--Color-text-normal, #161616)',
                    fontSize: pxToRem(14),
                    fontFamily: 'Pretendard, sans-serif',
                    fontWeight: '400',
                    lineHeight: pxToRem(21),
                    wordWrap: 'break-word'
                  }}>
                    {getRoleName(approval.roleId)}
                  </div>
                </div>
                {/* 포인트 */}
                <div style={{
                  width: pxToRem(139),
                  paddingLeft: pxToRem(8),
                  paddingRight: pxToRem(8),
                  paddingTop: pxToRem(12),
                  paddingBottom: pxToRem(12),
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  gap: pxToRem(8),
                  display: 'flex'
                }}>
                  <div style={{
                    color: 'var(--Color-text-normal, #161616)',
                    fontSize: pxToRem(14),
                    fontFamily: 'Pretendard, sans-serif',
                    fontWeight: '400',
                    lineHeight: pxToRem(21),
                    wordWrap: 'break-word'
                  }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: pxToRem(4) }}>
                      <span>{getPointAmount(approval.roleId) || 0}</span>
                      {getEstimatedNet &&
                        getEstimatedNet(approval.roleId) !== (getPointAmount(approval.roleId) || 0) && (
                          <span
                            style={{
                              fontSize: pxToRem(12),
                              color: 'var(--Color-text-assistive-strong, #4C4C4C)',
                            }}
                          >
                            실수령 예상 {getEstimatedNet(approval.roleId)}
                          </span>
                        )}
                    </div>
                  </div>
                </div>
                {/* 포인트 관리자 승인 */}
                <div style={{
                  width: pxToRem(290),
                  paddingLeft: pxToRem(8),
                  paddingRight: pxToRem(8),
                  paddingTop: pxToRem(12),
                  paddingBottom: pxToRem(12),
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  gap: pxToRem(4),
                  display: 'flex'
                }}>
                  {pointAutoPay ? (
                    <div style={{
                      color: 'var(--Color-status-positive-2, #00B66A)',
                      fontSize: pxToRem(14),
                      fontFamily: 'Pretendard, sans-serif',
                      fontWeight: '400',
                      lineHeight: pxToRem(21),
                      wordWrap: 'break-word'
                    }}>
                      자동 승인
                    </div>
                  ) : approval.pointManagerApproved === true ? (
                    <div style={{
                      color: 'var(--Color-status-positive-2, #00B66A)',
                      fontSize: pxToRem(14),
                      fontFamily: 'Pretendard, sans-serif',
                      fontWeight: '400',
                      lineHeight: pxToRem(21),
                      wordWrap: 'break-word'
                    }}>
                      승인 완료
                    </div>
                  ) : approval.pointManagerApproved === false ? (
                    <div style={{
                      color: 'var(--Color-status-negative, #FF3838)',
                      fontSize: pxToRem(14),
                      fontFamily: 'Pretendard, sans-serif',
                      fontWeight: '400',
                      lineHeight: pxToRem(21),
                      wordWrap: 'break-word'
                    }}>
                      미승인
                    </div>
                  ) : (
                    <div style={{
                      color: 'var(--Color-fill-normal, #293044)',
                      fontSize: pxToRem(14),
                      fontFamily: 'Pretendard, sans-serif',
                      fontWeight: '400',
                      lineHeight: pxToRem(21),
                      wordWrap: 'break-word'
                    }}>
                      대기중
                    </div>
                  )}
                </div>
                {/* 교사 최종 승인 */}
                <div style={{
                  width: pxToRem(290),
                  paddingLeft: pxToRem(8),
                  paddingRight: pxToRem(8),
                  paddingTop: pxToRem(12),
                  paddingBottom: pxToRem(12),
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  gap: pxToRem(4),
                  display: 'flex'
                }}>
                  {pointAutoPay ? (
                    <div style={{
                      color: 'var(--Color-status-positive-2, #00B66A)',
                      fontSize: pxToRem(14),
                      fontFamily: 'Pretendard, sans-serif',
                      fontWeight: '400',
                      lineHeight: pxToRem(21),
                      wordWrap: 'break-word'
                    }}>
                      자동 승인
                    </div>
                  ) : approval.teacherApproved === true ? (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: pxToRem(4)
                    }}>
                      {renderStatusIcon('approved')}
                      <div style={{
                        color: 'var(--Color-status-positive-2, #00B66A)',
                        fontSize: pxToRem(14),
                        fontFamily: 'Pretendard, sans-serif',
                        fontWeight: '400',
                        lineHeight: pxToRem(21),
                        wordWrap: 'break-word'
                      }}>
                        최종 승인 대기 중
                      </div>
                    </div>
                  ) : approval.teacherApproved === false ? (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: pxToRem(4)
                    }}>
                      {renderStatusIcon('rejected')}
                      <div style={{
                        color: 'var(--Color-status-negative, #FF3838)',
                        fontSize: pxToRem(14),
                        fontFamily: 'Pretendard, sans-serif',
                        fontWeight: '400',
                        lineHeight: pxToRem(21),
                        wordWrap: 'break-word'
                      }}>
                        미승인
                    </div>
                    </div>
                  ) : (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: pxToRem(4)
                    }}>
                      {renderStatusIcon('waiting')}
                      <div style={{
                        color: 'var(--Color-status-negative, #FF3838)',
                        fontSize: pxToRem(14),
                        fontFamily: 'Pretendard, sans-serif',
                        fontWeight: '400',
                        lineHeight: pxToRem(21),
                        wordWrap: 'break-word'
                      }}>
                        대기 중
                      </div>
                    </div>
                  )}
                </div>
                {/* 승인 버튼 */}
                <div style={{
                  width: pxToRem(110),
                  height: pxToRem(45),
                  paddingLeft: pxToRem(8),
                  paddingRight: pxToRem(8),
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  gap: pxToRem(8),
                  display: 'flex'
                }}>
                  {!pointAutoPay && !approval.isApproved && (
                    <div
                      onClick={() => handleApprove(approval.id)}
                      style={{
                        width: pxToRem(66),
                        height: pxToRem(25),
                        paddingLeft: pxToRem(8),
                        paddingRight: pxToRem(8),
                        paddingTop: pxToRem(4),
                        paddingBottom: pxToRem(4),
                        background: 'var(--Color-fill-normal, #293044)',
                        borderRadius: pxToRem(4),
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        cursor: 'pointer'
                      }}
                    >
                      <div style={{
                        color: 'var(--Color-text-inverse, white)',
                        fontSize: pxToRem(11),
                        fontFamily: 'Pretendard, sans-serif',
                        fontWeight: '400',
                        lineHeight: pxToRem(16.5),
                        wordWrap: 'break-word'
                      }}>
                        최종 승인
                      </div>
                    </div>
                  )}
                </div>
                {/* 관리 (삭제 아이콘) */}
                <div style={{
                  height: pxToRem(45),
                  paddingLeft: pxToRem(8),
                  paddingRight: pxToRem(8),
                  paddingTop: pxToRem(12),
                  paddingBottom: pxToRem(12),
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  gap: pxToRem(9),
                  display: 'flex'
                }}>
                  <div
                    onClick={() => handleDelete(approval.id)}
                    style={{
                      width: pxToRem(20),
                      height: pxToRem(20),
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      cursor: 'pointer'
                    }}
                  >
                    <TrashIcon width={16} height={16} color="#989898" />
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
      </div>
    </>
  );
};

export default ApprovalTable;
